import type { ExperienceEvent, ExperienceEventType } from '../types'

type EventCallback = (event: ExperienceEvent) => void

class ExperienceEventBus {
  private listeners: Map<string, Set<EventCallback>> = new Map()

  /**
   * Subscribe to a specific experience event type, or '*' for all events
   */
  public subscribe(type: ExperienceEventType | '*', callback: EventCallback): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set())
    }

    this.listeners.get(type)!.add(callback)

    // Return an unsubscribe function
    return () => {
      const set = this.listeners.get(type)
      if (set) {
        set.delete(callback)
        if (set.size === 0) {
          this.listeners.delete(type)
        }
      }
    }
  }

  /**
   * Publish an event to all interested subscribers
   */
  public publish(type: ExperienceEventType, experienceId: string, metadata?: Record<string, any>): void {
    const event: ExperienceEvent = {
      type,
      experienceId,
      timestamp: Date.now(),
      metadata,
    }

    // Call type-specific listeners
    const specificListeners = this.listeners.get(type)
    if (specificListeners) {
      specificListeners.forEach((callback) => {
        try {
          callback(event)
        } catch (err) {
          console.error(`[EventBus] Error in listener for type ${type}:`, err)
        }
      })
    }

    // Call catch-all listeners
    const catchAllListeners = this.listeners.get('*')
    if (catchAllListeners) {
      catchAllListeners.forEach((callback) => {
        try {
          callback(event)
        } catch (err) {
          console.error(`[EventBus] Error in catch-all listener for type ${type}:`, err)
        }
      })
    }
  }
}

export const EventBus = new ExperienceEventBus()
export default EventBus
